// Registry driver fiscali — la cassa non conosce i protocolli: aggiungere un modello = aggiungere un driver qui
import { epsonEposDriver } from "./epsonEpos";
import { rchPrintFDriver } from "./rchPrintF";
import { getPrinters } from "@/lib/usePrecontoPrinter";
import { pickPrinter, printerUsable } from "@/lib/pickPrinter";
import { getActiveStoreId } from "@/lib/storeContext";
import { sendHttpToLan, printChannelHint } from "@/lib/printTransport";
import { toast } from "sonner";
import { api } from "@/lib/api";

const MODEL_TO_DRIVER = {
  epson_fp81ii: epsonEposDriver,
  epson_tm_m30ii: epsonEposDriver,
  epson: epsonEposDriver,
  rch_printf3: rchPrintFDriver,
  rch: rchPrintFDriver,
};

export const getFiscalDriver = (printer) => (printer ? MODEL_TO_DRIVER[printer.model] || null : null);

// Ricostruisce l'elenco pagamenti dall'ordine chiuso (breakdown multiplo o metodo singolo)
export const paymentsFromOrder = (order) => {
  const bk = order?.payment_breakdown || {};
  const entries = Object.entries(bk).filter(([, v]) => Number(v) > 0);
  if (entries.length) return (Array.isArray(entries) ? entries : []).map(([method, amount]) => ({ method, amount: Number(amount) }));
  return [{ method: order?.payment_method || "cash", amount: Number(order?.total || 0) }];
};

// Individua stampante fiscale reale + driver del tenant
const findFiscal = async () => {
  const raw = await getPrinters();
  const printers = Array.isArray(raw) ? raw : [];
  const printer = pickPrinter(printers, "fiscal", getActiveStoreId());
  if (!printerUsable(printer)) return { error: { ok: false, reason: "no_fiscal_printer" } };
  const driver = getFiscalDriver(printer);
  if (!driver) return { error: { ok: false, reason: "no_driver", printer: printer.name } };
  return { printer, driver };
};

// Consegna un payload al driver. Epson ePOS: fetch HTTP diretto verso fpmate.cgi con
// parsing rigoroso della risposta — ok:true SOLO con success="true" esplicito.
// RCH e altri protocolli raw restano su QZ. ESC/POS (AXON POS80K preconto/comande) NON passa di qui.
const deliver = async (printer, driver, payload, _jobName) => {
  if (driver.transport === "http") {
    const configured = Number(printer.port) || 0;
    const ports = [...new Set([configured, 80, 9100].filter((n) => n > 0))];
    let res = { ok: false, reason: "send_failed" };
    const path = `/cgi-bin/fpmate.cgi?devid=${encodeURIComponent(printer.devid || "local_printer")}&timeout=10000`;
    for (const port of ports) {
      res = await sendHttpToLan({
        host: printer.ip,
        port,
        path,
        body: payload,
        contentType: "text/xml; charset=utf-8",
        timeoutMs: 8000,
      });
      if (res.ok && String(res.response || "").includes("success")) break;
    }
    if (!res.ok) {
      let detail = res.detail || res.reason;
      if (res.via === "fetch" && typeof window !== "undefined" && window.location.protocol === "https:") {
        detail += " — pagina HTTPS: usa il tablet (TCP nativo) o QZ Tray sul PC.";
      }
      return { ok: false, reason: res.reason || "send_failed", detail, driver: driver.id, printer: printer.name };
    }
    const text = res.response || "";
    if (!String(text).trim()) {
      return { ok: false, reason: "printer_error", detail: "risposta vuota da fpmate.cgi", driver: driver.id, printer: printer.name };
    }
    const m = String(text).match(/success\s*=\s*"(true|false)"/i);
    if (!m) {
      return { ok: false, reason: "printer_error", detail: `risposta senza esito (manca success): ${String(text).slice(0, 120)}`, driver: driver.id, printer: printer.name };
    }
    if (m[1].toLowerCase() !== "true") {
      const code = (String(text).match(/code\s*=\s*"([^"]*)"/i) || [])[1];
      const status = (String(text).match(/status\s*=\s*"([^"]*)"/i) || [])[1];
      return { ok: false, reason: "printer_error", detail: code || (status ? `status ${status}` : 'success="false"'), driver: driver.id, printer: printer.name };
    }
    return { ok: true, driver: driver.id, printer: printer.name, response: text, detail: "confermato dalla stampante", via: res.via };
  }
  const { sendRawTcp } = await import("@/lib/printTransport");
  const port = printer.port || driver.port || 9100;
  const raw = driver.toRaw ? driver.toRaw(printer, payload) : payload;
  const r = await sendRawTcp({ host: printer.ip, port, data: raw, encoding: driver.encoding || "ISO-8859-1" });
  if (!r.ok) return { ok: false, reason: r.reason || "send_failed", detail: r.detail, driver: driver.id, printer: printer.name };
  return { ok: true, driver: driver.id, printer: printer.name, via: r.via };
};

/**
 * Invia lo scontrino alla stampante fiscale configurata nel tenant usando il driver
 * corretto per il suo protocollo (Epson ePOS XML via HTTP, RCH Print!F via TCP).
 */
export const sendFiscalReceipt = async ({ tenant, order, payments }) => {
  const { printer, driver, error } = await findFiscal();
  if (error) return error;
  const pay = payments && payments.length ? payments : paymentsFromOrder(order);
  const payload = driver.buildReceipt({ tenant, order, payments: pay });
  const res = await deliver(printer, driver, payload, `Fiscale ${driver.label}`);
  // Riferimenti reali RT (nr. documento, Z, data) estratti dalla risposta: da salvare sull'ordine
  if (res.ok && res.response && driver.parseRt) res.rt = driver.parseRt(res.response);
  return res;
};

// Data documento in ora italiana "gg/mm/aaaa" (i riferimenti RT sono in ora locale)
const romeDate = (iso) =>
  new Intl.DateTimeFormat("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(iso || Date.now()));

const p4 = (n) => String(Math.max(0, parseInt(n, 10) || 0)).padStart(4, "0");

// Riferimenti del documento sulla RT: preferisce quelli reali salvati (rt_info), altrimenti i numeri app
const rtRefOf = (order) => {
  const rt = order?.rt_info || {};
  return {
    docNum: rt.fiscal_number ?? order?.receipt_number,
    zRep: rt.z_rep ?? order?.z_number,
    date: rt.fiscal_date || romeDate(order?.closed_at),
    fromPrinter: Boolean(rt.fiscal_number),
  };
};

// Esportato per precompilare il riferimento modificabile nella modale di annullo
export const fiscalRefOf = rtRefOf;

/**
 * Emette sulla RT il documento di annullamento riferito allo scontrino originale
 * (Epson: printRecMessage tipo 4 "VOID zzzz nnnn ggmmaaaa matricola").
 * `ref` opzionale {zRep, docNum, date "gg/mm/aaaa"} sovrascrive i riferimenti salvati.
 */
const filled = (v) => String(v ?? "").trim();
const dateOk = (d) => filled(d).replace(/\D/g, "").length === 8;

/** Un campo lasciato vuoto non cancella numero, chiusura Z o data già salvati. */
const resolveRtRef = async (order, ref) => {
  const base = rtRefOf(order);
  let zRep = filled(ref?.zRep) || filled(base.zRep);
  let docNum = filled(ref?.docNum) || filled(base.docNum);
  let date = filled(ref?.date) || filled(base.date);
  if ((!zRep || !docNum || !dateOk(date)) && order?.id) {
    try {
      const fresh = await api.get(`/orders/${order.id}`);
      const again = rtRefOf(fresh.data);
      zRep = zRep || filled(again.zRep);
      docNum = docNum || filled(again.docNum);
      if (!dateOk(date)) date = filled(again.date) || date;
    } catch { /* resta quello che c'è */ }
  }
  return { zRep, docNum, date };
};

export const sendFiscalVoid = async ({ order, ref }) => {
  const { printer, driver, error } = await findFiscal();
  if (error) return error;
  if (!driver.buildVoid) return { ok: false, reason: "no_capability", driver: driver.id, printer: printer.name };
  const serial = (printer.fiscal_serial || "").trim();
  if (!serial) return { ok: false, reason: "no_serial", driver: driver.id, printer: printer.name };
  const { zRep, docNum, date } = await resolveRtRef(order, ref);
  if (!docNum || !zRep || !dateOk(date)) {
    return { ok: false, reason: "no_rt_ref", driver: driver.id, printer: printer.name };
  }
  const dateDigits = date.replace(/\D/g, "");
  const payload = driver.buildVoid({ zRep, docNum, dateDDMMYYYY: dateDigits, serial });
  const res = await deliver(printer, driver, payload, `Annullo ${driver.label}`);
  res.voidRef = `VOID ${p4(zRep)} ${p4(docNum)} ${dateDigits} ${serial}`;
  return res;
};

/** Scontrino di reso parziale sulla RT (Epson printRecRefund). Lo storno totale usa sendFiscalVoid. */
export const sendFiscalRefund = async ({ order }) => {
  const { printer, driver, error } = await findFiscal();
  if (error) return error;
  if (!driver.buildRefund) return { ok: false, reason: "no_capability", driver: driver.id, printer: printer.name };
  const pay = paymentsFromOrder(order).map((p) => ({ ...p, amount: Math.abs(Number(p.amount) || 0) }));
  const payload = driver.buildRefund({ order, payments: pay });
  const res = await deliver(printer, driver, payload, `Reso ${driver.label}`);
  if (res.ok && res.response && driver.parseRt) res.rt = driver.parseRt(res.response);
  return res;
};

/** Ristampa dal DGFE della RT la copia conforme di un documento commerciale memorizzato. */
export const sendFiscalReprint = async ({ order }) => {
  const { printer, driver, error } = await findFiscal();
  if (error) return error;
  if (!driver.buildContentReprint) return { ok: false, reason: "no_capability", driver: driver.id, printer: printer.name };
  const { docNum, date } = rtRefOf(order);
  if (!docNum) return { ok: false, reason: "no_rt_ref", driver: driver.id, printer: printer.name };
  const [day, month, year] = date.split("/");
  return deliver(printer, driver, driver.buildContentReprint({ day, month, year, number: docNum }), `Ristampa ${driver.label}`);
};

/** Lettura X ("x") o Chiusura Z ("z") sulla stampante fiscale. */
export const sendFiscalReport = async (kind) => {
  const { printer, driver, error } = await findFiscal();
  if (error) return error;
  if (!driver.buildReport) return { ok: false, reason: "no_capability", driver: driver.id, printer: printer.name };
  return deliver(printer, driver, driver.buildReport(kind), `${kind === "z" ? "Chiusura Z" : "Lettura X"} ${driver.label}`);
};

/**
 * Ristampa dell'ultimo scontrino fiscale come DUPLICATO (comando nativo della stampante,
 * es. Epson printDuplicateReceipt): non registra una nuova vendita fiscale.
 */
export const sendFiscalDuplicate = async () => {
  const { printer, driver, error } = await findFiscal();
  if (error) return error;
  if (!driver.buildDuplicate) return { ok: false, reason: "no_duplicate", driver: driver.id, printer: printer.name };
  return deliver(printer, driver, driver.buildDuplicate(), `Duplicato ${driver.label}`);
};

// Messaggi leggibili per gli esiti negativi di sendFiscalReceipt (mostrati nei toast di cassa)
export const fiscalErrorMessage = (res) => {
  const map = {
    no_fiscal_printer: "Nessuna stampante fiscale reale (con IP). Il simulatore non stampa. Configura Epson/RCH sulla LAN del tablet.",
    no_driver: `Nessun driver per la stampante "${res.printer}"`,
    qz_offline: printChannelHint(),
    simulator: "Stampante in simulazione: nessuna carta e niente A4.",
    printer_error: `La stampante ha rifiutato il comando (${res.detail})`,
    http_error: `Errore HTTP dalla stampante: ${res.detail}`,
    send_failed: `Invio fallito: ${res.detail || "errore di rete"}`,
    no_serial: "Matricola RT mancante: aprila da Stampanti → stampante fiscale e inserisci la matricola (es. 99MEY012345)",
    no_rt_ref: "Riferimenti RT del documento non disponibili (numero/chiusura Z)",
    no_capability: `Funzione non supportata dal driver della stampante "${res.printer}"`,
  };
  return map[res.reason] || "Stampa fiscale non riuscita";
};

/** Dopo close-fiscal sul server: prova la carta sulla RT. Il popup verde del numero non è la stampa. */
export const emitFiscalPaper = async ({ tenant, order }) => {
  if (!order) return { ok: false, reason: "no_order" };
  try {
    const res = await sendFiscalReceipt({ tenant, order });
    if (res.ok) {
      toast.success(`Carta RT su "${res.printer}"${res.detail ? ` — ${res.detail}` : ""}`);
      if (res.rt && order.id) api.post(`/orders/${order.id}/rt-info`, res.rt).catch(() => {});
      return res;
    }
    toast.error(fiscalErrorMessage(res), { duration: 12000 });
    return res;
  } catch (pe) {
    toast.error(`Carta RT non uscita: ${String(pe?.message || pe)}`, { duration: 12000 });
    return { ok: false, reason: "send_failed", detail: String(pe?.message || pe) };
  }
};
